/**
 * Rounds a value to the nearest multiple of a given number.
 * This function is primarily designed for positive values (e.g., order quantities).
 * For negative values, the function returns the multiple itself.
 *
 * @param value - The value to round (should be positive for expected behavior)
 * @param multiple - The multiple to round to (must be positive)
 * @returns The value rounded to the nearest multiple, or the multiple itself if value is less than multiple
 *
 * @example
 * roundToNearestMultiple(23, 5) // returns 25
 * roundToNearestMultiple(12, 5) // returns 10
 * roundToNearestMultiple(3, 5) // returns 5
 * roundToNearestMultiple(15, 5) // returns 15
 * roundToNearestMultiple(2.5, 1) // returns 3 (rounds half up)
 */
export function roundToNearestMultiple(value: number, multiple: number): number {
  if (multiple <= 0) {
    return value;
  }

  if (value < multiple) {
    return multiple;
  }

  return Math.round(value / multiple) * multiple;
}

/**
 * Checks if a number is negative.
 *
 * @param value - The number to check
 * @returns True if the value is less than 0, false otherwise
 *
 * @example
 * isNegative(-5) // returns true
 * isNegative(0) // returns false
 * isNegative(5) // returns false
 */
export function isNegative(value: number): boolean {
  return value < 0;
}
